import { Drawer, Modal } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Percent } from "lucide-react";
import { useEcomConfig } from "hooks/useEcomConfig";
import { setSelectedGrandPromo } from "State/CartSlice/CartSlice";
import { RootState } from "State/store";

interface OrderDiscountSelectorProps {
  discounts: any[];
  baseFinalTotal: number;
}

const OrderDiscountSelector: React.FC<OrderDiscountSelectorProps> = ({
  discounts,
  baseFinalTotal,
}) => {
  const dispatch = useDispatch();
  const selectedGrandPromo = useSelector(
    (state: RootState) => state.cart.selectedGrandPromo
  );
  const { showPromotion, numberOfDecimal } = useEcomConfig();
  const [opened, setOpened] = useState(false);

  // Mirrors the exact conditions used to compute grandDiscountAmount in
  // MiniCartSidebar, PriceDetails, and PaymentMethod.
  // Returns true only when the promo would produce a non-zero discount.
  const isPromoEffective = (promo: any): boolean => {
    if (!promo) return false;
    if (!promo.promotionValue) return false;
    if (promo.isActive !== "Y") return false;
    if (baseFinalTotal < (promo.fromPrice || 0)) return false;
    if (baseFinalTotal > (promo.toPrice || Infinity)) return false;
    if (promo.promotionType === "INR" && promo.promotionValue >= baseFinalTotal) return false;
    return true;
  };

  // Auto-apply first effective discount by default if none is selected
  useEffect(() => {
    if (!showPromotion) return;

    const firstEffective = discounts.find((d) => isPromoEffective(d));
    if (firstEffective && !selectedGrandPromo) {
      dispatch(setSelectedGrandPromo(firstEffective));
    } else if (discounts.length === 0 && selectedGrandPromo) {
      dispatch(setSelectedGrandPromo(null));
    }
  }, [discounts, selectedGrandPromo, dispatch, showPromotion]);

  // Clear selected promo if it is no longer effective (e.g. INR value exceeds total)
  useEffect(() => {
    if (selectedGrandPromo && !isPromoEffective(selectedGrandPromo)) {
      dispatch(setSelectedGrandPromo(null));
    }
  }, [baseFinalTotal, selectedGrandPromo, dispatch]);

  if (!showPromotion) {
    return null;
  }

  if (!discounts || discounts.length === 0) {
    return null;
  }

  const handleApply = (promo: any) => {
    dispatch(setSelectedGrandPromo(promo));
    setOpened(false);
  };

  const handleRemove = () => {
    dispatch(setSelectedGrandPromo(null));
  };

  const activePromo = selectedGrandPromo;

  return (
    <div className="mt-4 mb-4">
      <div
        onClick={() => setOpened(true)}
        className="flex items-center justify-between p-3 border border-pink-200 bg-pink-50 rounded-lg cursor-pointer hover:bg-pink-100 transition shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-pink-100 text-pink-600 rounded-md">
            <Percent size={20} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-800">
              Coupons & Offers
            </h4>
            {activePromo && isPromoEffective(activePromo) ? (
              <p className="text-xs text-green-600 font-medium">
                1 Offer Applied
              </p>
            ) : (
              <p className="text-xs text-pink-600 font-medium">
                Apply now and save extra!
              </p>
            )}
          </div>
        </div>
        <div className="text-pink-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </div>
      </div>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <div className="text-lg font-bold text-gray-800">
            Coupons & Offers
          </div>
        }
        centered
        size="md"
        zIndex={2000}
        styles={{
          header: { borderBottom: "1px solid #eee", paddingBottom: "10px" },
          body: { padding: "16px", backgroundColor: "#f9fafb" },
        }}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-2">
            Other Coupons
            <br />
            <span className="text-xs text-gray-500">
              Select an offer to get additional discounts
            </span>
          </div>

          {discounts.map((promo, index) => {
            const effective = isPromoEffective(promo);
            const isApplied = activePromo?.id === promo.id && effective;
            const discountText =
              promo.promotionType === "%"
                ? `${Number(promo.promotionValue).toFixed(numberOfDecimal)}% off`
                : `₹${Number(promo.promotionValue).toFixed(numberOfDecimal)} off`;

            return (
              <div
                key={promo.id || index}
                className={`bg-white rounded-xl shadow-sm overflow-hidden border ${
                  isApplied ? "border-green-400" : effective ? "border-pink-100" : "border-gray-200"
                }`}
              >
                <div className={`p-4 relative ${!effective ? "opacity-70" : ""}`}>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${effective ? "bg-pink-50 text-pink-500" : "bg-gray-100 text-gray-400"}`}>
                        <Percent size={24} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-md font-bold text-gray-800">
                        Extra {discountText}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {promo.discountDesc || "Applicable on your order"}
                      </p>
                    </div>
                  </div>

                  <div className={`mt-4 p-3 border border-dashed rounded-lg flex items-center justify-between ${effective ? "border-pink-300 bg-pink-50/50" : "border-gray-300 bg-gray-50"}`}>
                    <span className="text-sm font-semibold text-gray-700 tracking-wider">
                      {promo.discountDesc?.substring(0, 10).toUpperCase() || "OFFER"}
                    </span>
                    {isApplied ? (
                      <button
                        onClick={() => handleRemove()}
                        className="text-sm font-bold text-green-600 flex items-center gap-1 hover:text-green-700"
                      >
                        Applied
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </button>
                    ) : effective ? (
                      <button
                        onClick={() => handleApply(promo)}
                        className="text-sm font-bold text-pink-600 hover:text-pink-700 transition"
                      >
                        Apply
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        Not applicable
                      </span>
                    )}
                  </div>
                </div>
                {!effective && (
                  <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 font-medium">
                    Shop more to use this coupon
                  </div>
                )}
                {isApplied && (
                  <div className="bg-green-50 px-4 py-2 text-xs text-green-700 font-medium">
                    Hurray! You saved extra on this order.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default OrderDiscountSelector;
